import { AC_CARD_STATUSES, type AcCardStatus, type AccreditationSummary } from "@seagames/contracts";
import type { AccreditationFilter } from "../../lib/api/accreditations";

/**
 * The dashboard's filters, as they sit in its URL (`/_app/` search): only
 * what `GET /accreditations/summary` filters by. `status` is ONE status here
 * (her select); the wire takes a comma list, so one value is a valid list.
 */
export interface DashboardSearch {
	games_id?: string;
	responsible_organization_id?: string;
	main_function_id?: string;
	sub_function_id?: string;
	status?: AcCardStatus;
}

export const DASHBOARD_FILTER_KEYS = ["games_id", "responsible_organization_id", "main_function_id", "sub_function_id", "status"] as const satisfies ReadonlyArray<
	keyof DashboardSearch
>;

/** URL → the summary's wire filter. Equal filters make equal query keys (`accreditationKeys.summary`). */
export const toSummaryFilter = (s: DashboardSearch): AccreditationFilter => ({
	games_id: s.games_id,
	responsible_organization_id: s.responsible_organization_id,
	main_function_id: s.main_function_id,
	sub_function_id: s.sub_function_id,
	status: s.status,
});

/**
 * View Records' search for a drill-down: the dashboard's filters, with
 * `status` (and anything else) overridden. Keys and shapes are the records
 * route's own (`accreditation.records.tsx` validateSearch): `status` is the
 * wire's comma list, `no_photo` a boolean.
 */
export interface RecordsSearch {
	games_id?: string;
	responsible_organization_id?: string;
	main_function_id?: string;
	sub_function_id?: string;
	status?: string;
	no_photo?: boolean;
}

export function recordsSearch(s: DashboardSearch, over: RecordsSearch = {}): RecordsSearch {
	const out: RecordsSearch = {
		games_id: s.games_id,
		responsible_organization_id: s.responsible_organization_id,
		main_function_id: s.main_function_id,
		sub_function_id: s.sub_function_id,
		status: s.status,
		...over,
	};
	for (const k of Object.keys(out) as Array<keyof RecordsSearch>) if (out[k] === undefined) delete out[k];
	return out;
}

/** Count per status, every status present (0 when the summary has no row for it). */
export function statusCounts(summary: AccreditationSummary | undefined): Record<AcCardStatus, number> {
	const by = Object.fromEntries(AC_CARD_STATUSES.map((s) => [s, 0])) as Record<AcCardStatus, number>;
	for (const row of summary?.by_status ?? []) by[row.status] = row.count;
	return by;
}

/** Her `pct()`: a whole percentage, 0 of 0 = 0. */
export const pct = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0);
